import { WithBusyIndicator, withBusyIndicator } from "@components/busyIndicator/withBusyIndicator";
import { HotspotViewIds } from "@components/hotspots/HotspotViewIds";
import { IEditableAmountDialogData } from "@components/smart/selectionTableView/EditableAmountDialog";
import SelectionTableViewBase, {
    ISelectionTableViewProps,
    THeaderDataGetter
} from "@components/smart/selectionTableView/SelectionTableViewBase";
import { IRow, TId } from "@components/table";
import { IRowProps } from "@components/table/Rows.types";
import BindingContext from "@odata/BindingContext";
import { getEntitySetByDocumentType } from "@odata/EntityTypes";
import { EntitySetName, EntityTypeName, IPrPayslipEntity } from "@odata/GeneratedEntityTypes";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { universalPersonNameFormatter } from "@pages/admin/users/Users.utils";
import { getEditableAmountDialogData } from "@pages/payroll/payslip/Payslip.utils";
import memoizeOneWithParamsCheck from "@utils/memoizeOne";
import i18next from "i18next";

export interface IPayslipPairDocumentTableViewProps extends ISelectionTableViewProps, WithBusyIndicator {
}

class PayslipPairDocumentTableView extends SelectionTableViewBase<IPayslipPairDocumentTableViewProps> {
    _hotspotContextId = HotspotViewIds.PairingPayslipDocTableView;

    get title(): string {
        return i18next.t("Payslip:Form.Title");
    }

    get headerData(): THeaderDataGetter {
        const { Employee, Employments } = this.getPayslip();
        const employmentTypes = Employments.map(({ Employment }) =>
                Employment?.TypeCode ? getEnumDisplayValue(EntityTypeName.PrEmploymentType, Employment?.TypeCode) : null);

        return [{
            label: i18next.t("Payslip:Pair.Employee"),
            value: universalPersonNameFormatter(Employee)
        }, {
            label: i18next.t("Payslip:Pair.Type"),
            value: employmentTypes.filter(t => !!t).join(", ")
        }];
    }

    get secondaryTitle(): string {
        return this.props.storage.data.definition.title;
    }

    get selectionConfirmText(): string {
        return i18next.t("Payslip:Pair.Connect");
    }

    get actionIconName(): string {
        const { storage } = this.props;
        return storage.data.bindingContext.getEntitySet().getName() === EntitySetName.OtherLiabilities
                ? "ConnectLiability"
                : "ConnectReceivable";
    }

    getEditableAmountDialogData(row: IRow, pairedDialogItemId: string): IEditableAmountDialogData {
        return getEditableAmountDialogData(this.storage, row.customData.entity, pairedDialogItemId);
    }

    getPayslip(): IPrPayslipEntity {
        return this.props.rootStorage.getEntity<IPrPayslipEntity>();
    }

    getRowEntitySet = (row: IRow): EntitySetName => {
        return getEntitySetByDocumentType(row.customData.entity.DocumentTypeCode);
    };

    getCorrectBindingContext = (bindingContext: BindingContext, row: IRow): BindingContext => {
        return bindingContext.createNewBindingContext(this.getRowEntitySet(row)).addKey(bindingContext.getKey());
    };

    // change the bc different caused by "getCorrectBindingContext" back to the entity set of the table rows
    getSelectedRows = memoizeOneWithParamsCheck(() => {
        const oldBc = this.props.storage.data.rowBindingContext;
        const bc = oldBc?.createNewBindingContext(this.props.storage.data.bindingContext.getPath()).addKey(oldBc.getKey());

        return bc ? [bc] : [];
    }, () => [this.props.storage.data.rowBindingContext]);

    handleRowActionClick = (rowId: TId, row: IRowProps): void => {
        const { storage } = this;
        const { entity } = row.customData;

        const { pairedItems } = storage.getCustomData();
        const key = rowId.toString();

        if (pairedItems?.[key]) {
            // row is selected -> unselect, clear data
            delete pairedItems[key];
        } else {
            // row is not selected -> select, set data
            const pairedItem = {
                TransactionAmount: entity?.TransactionAmountDue,
                ExchangeRatePerUnit: entity?.ExchangeRatePerUnit,
                TransactionCurrency: entity?.TransactionCurrency,
                Amount: entity?.TransactionAmountDue * entity?.ExchangeRatePerUnit
            };
            storage.setCustomData({ pairedItems: { ...(pairedItems ?? {}), [key]: pairedItem } });
        }
    }
}

export default withBusyIndicator({ passBusyIndicator: true })(PayslipPairDocumentTableView);